import { BackLink } from '@avanta/shared/components/BackLink';
import { PasswordRequirement } from '@avanta/shared/components/PasswordRequirement';
import { Button } from '@avanta/shared/components/ui/Button';
import { Input } from '@avanta/shared/components/ui/Input';
import { Spinner } from '@avanta/shared/components/ui/Spinner';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { VALIDATION_CODE_TYPE } from '@avanta/shared/constants/enterprise';
import { PATIENT_SERVICES } from '@avanta/shared/constants/patients';
import { UserType } from '@avanta/shared/constants/users';
import { useFetchCentersByEnterprise } from '@avanta/shared/hooks/useFetchCentersByEnterprise';
import { useFetchDepartments } from '@avanta/shared/hooks/useFetchDepartments';
import { useUpdatePatient } from '@avanta/shared/hooks/useUpdatePatient';
import { type Employee } from '@avanta/shared/types/employees';
import type { Patient } from '@avanta/shared/types/patients';
import type { User } from '@avanta/shared/types/users';
import { cn } from '@avanta/shared/utils/cn';
import { dateFromBirthParts, getBirthDateParts } from '@avanta/shared/utils/date';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { type Control, type FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { QUERY_KEYS } from 'constants/common';
import { REFERRAL } from 'constants/referral';
import { ROUTES } from 'constants/routes';
import { useFetchEnterprise } from 'hooks/useFetchEnterprise';
import { useStepForm } from 'hooks/useStepForm';
import { useUserStore } from 'stores/user';
import { type FormData as GenericFormData } from 'types/form';
import {
  getReferralParamsFromSession,
  getReferralParamsFromUrl,
  hasReferralParamsInUrl,
  saveReferralParamsToSession,
} from 'utils/referral';
import { ProgressBarForm } from './components/ProgressBarForm';
import { useRegisterUser } from './hooks/useRegisterUser';
import { useUpdateEmployee } from './hooks/useUpdateEmployee';
import {
  FIRST_STEP,
  initialFormData,
  showOnlyInSignUp,
  signupFormConfig,
  employeeSignupSteps,
  patientSignupSteps,
  profileSetupSteps,
  getFieldPlaceholder,
} from './model';
import { type StepsConfig, type FormData, type SignupFormProps } from './types';

const getSignupFormValues = (user?: User | null): FormData => ({
  ...initialFormData,
  ...user,
  gender: user?.gender?.toString() ?? '',
  dateOfBirth: dateFromBirthParts(user?.monthOfBirth, user?.yearOfBirth, user?.dayOfBirth),
  departmentId: user?.departmentId ?? '',
});

//TODO: Refactor component to use architecure components like InsuredPersonProfileForm
export const SignupForm = ({ isProfileSetup = false }: SignupFormProps): React.ReactElement => {
  const queryClient = useQueryClient();
  const { t } = useTranslation(LOCIZE_NS.AUTH);
  const [searchParams] = useSearchParams();
  const enterpriseId = searchParams.get('cid');
  const isFetchingUser = queryClient.isFetching({ queryKey: [QUERY_KEYS.FETCH_PATIENT] });
  const user = useUserStore(state => state.user);
  const { data: enterprise } = useFetchEnterprise({ enterpriseId, isProfileSetup });
  const navigate = useNavigate();
  const nifFieldName = signupFormConfig.nif.name;

  const getStepsConfig = (): StepsConfig => {
    if (isProfileSetup) return profileSetupSteps;
    if (enterpriseId) return employeeSignupSteps;
    return patientSignupSteps;
  };

  const stepsConfig = getStepsConfig();
  const totalSteps = Object.keys(stepsConfig).length;

  const {
    register,
    control,
    handleSubmit,
    trigger,
    formState: { errors, isValid },
    watch,
    reset,
    setValue,
  } = useForm<FormData>({
    defaultValues: getSignupFormValues(user),
    mode: 'onChange',
  });

  const isFamilyMember = Boolean(watch('familyMember'));

  const { step, progress, previousStep, currentFields, isLastStep, nextStep, goToStep } =
    useStepForm(
      {
        steps: stepsConfig,
        totalSteps,
      },
      getSignupFormValues(user)
    );

  const { mutate: updateEmployee, isPending: isUpdatingEmployee } = useUpdateEmployee();
  const { mutate: updatePatient, isPending: isUpdatingPatient } = useUpdatePatient();
  const { mutate: registerUser, isPending: isRegistering } = useRegisterUser({
    onError: () => {
      goToStep(FIRST_STEP);
    },
  });
  const isPending = isUpdatingEmployee || isUpdatingPatient || isRegistering;

  const { data: centers } = useFetchCentersByEnterprise({
    enterpriseId: enterprise?.id || '',
    isProfileSetup,
  });

  const { data: departments } = useFetchDepartments({
    centerId: watch('centerId'),
    isProfileSetup,
  });
  const singleCenterId = centers?.length === 1 ? (centers[0]?.value ?? null) : null;
  const singleDepartmentId = departments?.length === 1 ? (departments[0]?.value ?? null) : null;

  const optionsValues = {
    centerId: centers,
    departmentId: departments,
  };

  useEffect(() => {
    if (user && Object.keys(user).length > 0) {
      reset(getSignupFormValues(user));
    }
  }, [user, reset]);

  useEffect(() => {
    if (!isProfileSetup && hasReferralParamsInUrl()) {
      saveReferralParamsToSession(getReferralParamsFromUrl());
    }
  }, [isProfileSetup]);

  useEffect(() => {
    if (!singleCenterId) return;

    setValue('centerId', singleCenterId, { shouldValidate: true });
  }, [singleCenterId, setValue]);

  useEffect(() => {
    if (!singleDepartmentId) return;

    setValue('departmentId', singleDepartmentId, { shouldValidate: true });
  }, [singleDepartmentId, setValue]);

  const handleOnSubmit = async (data: FormData): Promise<void> => {
    if (!isLastStep) {
      const fieldsToValidate = currentFields.filter(shouldShowField) as (keyof FormData)[];
      const valid = await trigger(fieldsToValidate);
      if (!valid) return;
      nextStep();
      return;
    }
    const { dateOfBirth: birthDate, ...restData } = data;

    const dataToUpdate = {
      ...user,
      ...restData,
      spokenLanguages: [data.appLanguage],
      gender: Number(data.gender),
      ...(birthDate && getBirthDateParts(birthDate)),
    };
    if (isProfileSetup) {
      if (!user?.id) throw new Error(t('SignupForm.userId.error'));
      if (user.type === UserType.EMPLOYEE) {
        updateEmployee(dataToUpdate as Employee);
      }
      if (user.type === UserType.PATIENT) {
        updatePatient(dataToUpdate as Patient);
      }
      navigate(ROUTES.HOME);
      return;
    }

    const type = enterpriseId ? UserType.EMPLOYEE : UserType.PATIENT;
    const isPatient = type === UserType.PATIENT;

    const commonData: Record<string, unknown> = {
      email: data.email,
      password: data.password,
      nickname: data.nickname,
      gender: Number(data.gender),
      ...(birthDate && getBirthDateParts(birthDate)),
      timeZone: data.timeZone,
      spokenLanguages: [data.appLanguage],
      appLanguage: data.appLanguage,
      profileSetup: true,
    };

    const referralParams = getReferralParamsFromSession();
    const hasReferral = Boolean(referralParams.campaign || referralParams.promoterId);
    const patientSpecificData: Record<string, unknown> = {
      type: 'PatientToRegisterDTO',
      pushNotificationsId: '',
      activeServices: [PATIENT_SERVICES.PRIVATE],
      ...(hasReferral && {
        campaign: referralParams.campaign || REFERRAL.DEFAULT_CAMPAIGN_REFERRALS,
        promoterId: referralParams.promoterId || '',
      }),
    };

    let employeeSpecificData: Record<string, unknown> = {
      type: 'EmployeeToRegisterDTO',
      enterpriseId: enterpriseId || '',
      centerId: data.centerId,
      departmentId: data.departmentId,
      appVersion: 999,
      familyMember: data.familyMember,
      ...(data.familyMember &&
        data.nif && {
          promoterId: data.nif,
        }),
    };

    if (data.nif && enterprise?.requiresCodeValidation) {
      employeeSpecificData = { ...employeeSpecificData, nif: data.nif };
    }

    const body: Record<string, unknown> = isPatient
      ? { ...commonData, ...patientSpecificData }
      : { ...commonData, ...employeeSpecificData };

    registerUser({
      body,
    });
  };

  const shouldShowField = (id: string): boolean => {
    if (id === 'familyMember' && Boolean(enterpriseId) && !isProfileSetup) {
      return (
        Boolean(enterprise?.contract?.familyCoverage?.enabled) &&
        Boolean(enterprise?.requiresCodeValidation)
      );
    }
    if (isProfileSetup && showOnlyInSignUp.includes(id)) return false;
    if (!enterpriseId && (showOnlyInSignUp.includes(id) || id === 'enterpriseId')) return false;
    if (!enterprise?.requiresCodeValidation && id === nifFieldName) return false;
    const input = signupFormConfig[id];
    if (input?.userTypes) {
      const currentType = enterpriseId ? UserType.EMPLOYEE : UserType.PATIENT;
      if (!input.userTypes.includes(currentType)) return false;
    }
    return true;
  };

  const isFieldRequired = (id: string, defaultRequired: boolean): boolean => {
    if (!enterpriseId && ['centerId', 'departmentId', 'enterpriseId'].includes(id)) return false;
    return defaultRequired;
  };

  return (
    <>
      <ProgressBarForm
        step={step}
        totalSteps={totalSteps}
        progress={progress}
        onPreviousStep={previousStep}
        isProfileSetup={isProfileSetup}
      />
      {isFetchingUser ? (
        <div className="flex justify-center items-center h-105">
          <Spinner />
        </div>
      ) : (
        <form onSubmit={handleSubmit(handleOnSubmit)} className="space-y-5 mt-3">
          <div className="space-y-4">
            {currentFields.filter(shouldShowField).map(id => {
              const input = signupFormConfig[id];
              if (!input) return null;
              const formId = id as keyof FormData;
              const { component: Component } = input;
              const formErrors = errors[formId];
              const options = optionsValues as Record<string, { label: string; value: string }[]>;
              const isControllerComponent = Component !== Input;
              const isDisabled =
                typeof input.disabled === 'function'
                  ? input.disabled((watch() || {}) as unknown as GenericFormData, isProfileSetup)
                  : input.disabled || false;
              const fieldRequired = isFieldRequired(id, Boolean(input.required));

              if (isProfileSetup && showOnlyInSignUp.includes(id)) {
                return null;
              }

              if (!enterprise?.requiresCodeValidation && id === nifFieldName) {
                return null;
              }

              const passwordValue = formId === 'password' ? watch('password') : '';
              const useEmployeeCodeLabels =
                id === nifFieldName &&
                enterprise?.codeTypeValidation === VALIDATION_CODE_TYPE.EMPLOYEE_CODE;
              const label = useEmployeeCodeLabels
                ? t('SignupForm.Form.nifEmployeeCode.label')
                : t(input.label);
              const placeholder = getFieldPlaceholder({
                fieldId: id,
                placeholderKey: input.placeholder,
                useEmployeeCodeLabels,
                isFamilyMember,
              });
              const translatedPlaceholder = t(placeholder);

              const helperText =
                formErrors?.message && formId !== 'password' ? t(formErrors.message) : undefined;

              const rules = {
                required: fieldRequired,
                ...(input.pattern && { pattern: input.pattern }),
                ...(input.validate && { validate: input.validate }),
                ...(input.minLength && { minLength: input.minLength }),
                ...(input.maxLength && { maxLength: input.maxLength }),
              };

              return (
                <div key={id}>
                  {isControllerComponent ? (
                    <Component
                      control={control as unknown as Control<FieldValues>}
                      name={formId}
                      className={cn('w-full', input.className)}
                      label={label}
                      placeholder={translatedPlaceholder}
                      options={options?.[formId] ?? input.options ?? []}
                      rules={rules}
                      helperText={helperText}
                      variant={formErrors ? 'invalid' : 'filled'}
                      required={fieldRequired}
                      disabled={isDisabled}
                      showPassword={Boolean(input?.showPassword)}
                      autoComplete={input.autocomplete}
                      calendarProps={input.calendarProps}
                    />
                  ) : (
                    <Input
                      type={input.type}
                      className={cn('w-full', input.className)}
                      label={label}
                      placeholder={translatedPlaceholder}
                      helperText={helperText}
                      variant={formErrors ? 'invalid' : 'filled'}
                      showPassword={Boolean(input?.showPassword)}
                      disabled={isDisabled}
                      {...register(formId, rules)}
                      autoComplete={input.autocomplete}
                    />
                  )}
                  {formId === 'password' && <PasswordRequirement password={passwordValue} />}
                </div>
              );
            })}
          </div>
          <Button type="submit" className="w-full mt-2" disabled={isPending || !isValid}>
            {!isLastStep
              ? t('SignupForm.next')
              : isProfileSetup
                ? t('SignupForm.updateProfile')
                : t('SignupForm.register')}
          </Button>
          {!isProfileSetup && step === FIRST_STEP && (
            <div className="mt-2 flex justify-center">
              <BackLink label={t('Register.backToLogin')} to={ROUTES.LOGIN} />
            </div>
          )}
        </form>
      )}
    </>
  );
};
